import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _typeof from '@babel/runtime/helpers/esm/typeof';
import React from 'react';
import { describe } from 'react-desc';
import { get } from 'lodash';
import styled from 'styled-components';
import { flexbox, justifyContent, space, height, width } from 'styled-system';
import { fixSpaceGutter, theme, mapTemplateGrid, mapGap } from '@elliemae/ds-system';
import { positionIE11Grid } from './ie11.js';
import { gridProps } from './props.js';
import { defaultProps } from './defaultProps.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/* eslint-disable complexity */

/* stylelint-disable property-no-vendor-prefix */

var GridItem = /*#__PURE__*/styled.div.withConfig({
  componentId: "qbn2kv-0"
})(["display:grid;display:-ms-grid;", " ", " ", " ", " ", " ", " ", " ", " -ms-grid-rows:", ";grid-template-rows:", ";column-gap:", ";row-gap:", ";"], flexbox, justifyContent, space, height, width, positionIE11Grid, function (props) {
  if (_typeof(props.cols) === 'object' && !Array.isArray(props.cols)) {
    var data = '';

    for (var key in theme.mediaQueries) {
      // using grid approach
      if (props.cols[key] && Array.isArray(props.cols[key])) {
        var mtg = mapTemplateGrid(props.cols[key]).join(' ');
        var mtgs = mapTemplateGrid(props.cols[key]).map(function (c) {
          return "minmax(0, ".concat(c, ")");
        }).join(' ');
        data += "@media ".concat(theme.mediaQueries[key], " {\n            -ms-grid-columns: ").concat(mtg || 'minmax(0px, 1fr)', ";\n            grid-template-columns: ").concat(mtg || 'auto', ";\n            -ms-grid-columns: ").concat(mtgs || 'minmax(0px, 1fr)', ";\n            grid-template-columns: ").concat(mtgs || 'auto', ";\n          }");
      } else if (props.isSpanParent) {
        data += "@media ".concat(theme.mediaQueries[key], " {\n              -ms-grid-columns: repeat(").concat(props.cols[key], ", 1fr);\n              grid-template-columns: repeat(").concat(props.cols[key], ", 1fr);\n            }");
      }
    }

    return data;
  }

  if (Array.isArray(props.cols)) {
    var _mtg = mapTemplateGrid(props.cols).join(' ');

    var _mtgs = mapTemplateGrid(props.cols).map(function (c) {
      return "minmax(0, ".concat(c, ")");
    }).join(' ');

    return "\n      -ms-grid-columns: ".concat(_mtg || 'minmax(0px, 1fr)', ";\n      grid-template-columns: ").concat(_mtg || 'auto', ";\n      -ms-grid-columns: ").concat(_mtgs || 'minmax(0px, 1fr)', ";\n      grid-template-columns: ").concat(_mtgs || 'auto', ";\n      ");
  }

  if (props.isSpanParent) {
    return "\n      -ms-grid-columns: repeat(".concat(props.cols, ", 1fr);\n      grid-template-columns: repeat(").concat(props.cols, ", 1fr);");
  }

  return '';
}, function (props) {
  if (!props.isSpanParent) return '';

  if (_typeof(props.span) === 'object') {
    var data = '';

    for (var key in theme.mediaQueries) {
      if (props.span[key] && theme.mediaQueries[key]) {
        data += "@media ".concat(theme.mediaQueries[key], " {\n            -ms-grid-column: auto / span ").concat(props.span[key], ";\n            grid-column: auto / span ").concat(props.span[key], ";\n          }");
      }
    }

    return data;
  }

  return "\n    -ms-grid-column: auto / span ".concat(props.span, ";\n    grid-column: auto / span ").concat(props.span, ";");
}, function (props) {
  return mapTemplateGrid(props.rows).join(' ') || 'minmax(0px, 1fr)';
}, function (props) {
  return mapTemplateGrid(props.rows).join(' ') || 'auto';
}, function (props) {
  return mapGap(props.gutter);
}, function (props) {
  return mapGap(props.gutter);
});
/* stylelint-enable property-no-vendor-prefix */

/* eslint-enable complexity */

var Grid = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var rows = _ref.rows,
      cols = _ref.cols,
      alignItems = _ref.alignItems,
      children = _ref.children,
      className = _ref.className,
      direction = _ref.direction,
      justify = _ref.justify,
      gutter = _ref.gutter,
      wrap = _ref.wrap,
      rest = _objectWithoutProperties(_ref, ["rows", "cols", "alignItems", "children", "className", "direction", "justify", "gutter", "wrap"]);

  var isSpanParent = true;
  var renderChildren = React.Children.map(children, function (child) {
    if (get(child, 'child.type.name') === 'Grid' || get(child, 'type.displayName') === 'Grid' || parseInt(get(child, 'props.span', 0), 10) > 0) {
      var _child$props = child.props,
          wc = _child$props.width,
          hc = _child$props.height,
          span = _child$props.span;
      if (!span) isSpanParent = false;
      return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
        width: fixSpaceGutter(wc),
        // eslint-disable-next-line max-lines
        height: fixSpaceGutter(hc)
      }));
    }

    return child;
  });
  return /*#__PURE__*/React.createElement(GridItem, _extends({
    alignItems: alignItems,
    className: className,
    cols: cols,
    direction: direction,
    gutter: gutter,
    isSpanParent: isSpanParent,
    justify: justify,
    rows: rows,
    wrap: wrap,
    childNumber: React.Children.count(children)
  }, rest, {
    ref: ref
  }), renderChildren);
});
Grid.displayName = 'Grid';
Grid.propTypes = gridProps;
Grid.defaultProps = defaultProps;
var DSGridWithSchema = describe(Grid).description('Grid layout helper component');
DSGridWithSchema.propTypes = gridProps;

export default Grid;
export { DSGridWithSchema };
//# sourceMappingURL=Grid.js.map
